import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { MobileNav } from "@/components/MobileNav";
import { SponsorStrip } from "@/components/SponsorStrip";
import { toast } from "sonner";

function initials(name) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export default function Circoli() {
  const [clubs, setClubs] = useState(null);

  useEffect(() => {
    api
      .get("/clubs")
      .then((res) => setClubs(res.data))
      .catch(() => toast.error("Impossibile caricare i circoli"));
  }, []);

  const goldCount = clubs ? clubs.filter((c) => c.teams.some((t) => t.league === "gold")).length : 0;
  const silverCount = clubs ? clubs.filter((c) => c.teams.some((t) => t.league === "silver")).length : 0;

  return (
    <div className="bg-[#e3edf6] text-[#0f172a] min-h-screen">
      <nav className="border-b border-[#e2e8f0] px-6 md:px-8 py-5 sticky top-0 bg-[#e3edf6]/90 backdrop-blur-md z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img src="/logo-coppa.png" alt="Coppa dei Club" className="h-8 w-auto" />
            <span className="text-[#0284c7] font-mono-x text-xs">/ Hub</span>
          </Link>
          <div className="flex items-center gap-2 text-xs font-ui text-[#475569]">
            <a href="/#calendari" data-testid="nav-calendari" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Calendari Gare
            </a>            <Link to="/circoli" data-testid="nav-circoli" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Circoli
            </Link>
            <Link to="/galleria" data-testid="nav-galleria" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Galleria foto
            </Link>

            <a href="/#regolamento" data-testid="nav-regolamento" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Regolamento
            </a>
            <Link to="/modulistica" data-testid="nav-modulistica" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Modulistica
            </Link>
            <Link
              to="/admin"
              data-testid="nav-admin-login"
              className="hidden sm:inline-block px-4 py-2 bg-[#e2e8f0] text-[#0f172a] hover:bg-[#38bdf8] hover:text-[#0f172a] transition-all rounded font-semibold border border-[#cbd5e1]"
            >
              Login Admin
            </Link>
            <MobileNav active="circoli" />
          </div>
        </div>
      </nav>

      <header className="py-16 md:py-20 px-6 md:px-8 border-b border-[#e2e8f0]">
        <div className="max-w-6xl mx-auto">
          <span className="font-mono-x text-xs text-[#0284c7] tracking-widest uppercase block mb-2">
            Coppa dei Club 2026-27
          </span>
          <h1 className="font-display text-4xl md:text-5xl text-[#0f172a] mb-4">Circoli Partecipanti</h1>
          <p className="text-base text-[#475569] max-w-2xl leading-relaxed">
            {clubs
              ? `${clubs.length} circoli in gara: ${goldCount} con squadre in Girone Gold e ${silverCount} in Girone Silver. I loghi ufficiali vengono pubblicati man mano che i circoli li inviano.`
              : "Caricamento dei circoli..."}
          </p>
        </div>
      </header>

      <SponsorStrip />

      <main className="py-16 px-6 md:px-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="clubs-grid">
          {clubs &&
            clubs.map((club) => (
              <div
                key={club.slug}
                data-testid={`club-card-${club.slug}`}
                className="p-5 bg-white border border-[#e2e8f0] rounded-xl hover:border-[#cbd5e1] transition-colors"
              >
                <div className="flex items-center gap-4 mb-4">
                  {club.logo ? (
                    <img
                      src={club.logo}
                      alt={`Logo ${club.name}`}
                      loading="lazy"
                      data-testid={`club-logo-${club.slug}`}
                      className="w-[92px] h-[92px] rounded-lg object-contain bg-[#e3edf6] border border-[#e2e8f0] p-1.5"
                    />
                  ) : (
                    <div
                      data-testid={`club-logo-${club.slug}`}
                      className="w-[92px] h-[92px] rounded-lg bg-[#e3edf6] border border-[#e2e8f0] flex items-center justify-center font-display text-2xl text-[#0284c7]"
                    >
                      {initials(club.name)}
                    </div>
                  )}
                  <h2 className="font-bold text-[#0f172a] text-base leading-tight">{club.name}</h2>
                </div>
                {club.venue && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(club.venue)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`club-venue-${club.slug}`}
                    className="inline-flex items-center gap-1.5 text-[11px] text-[#475569] hover:text-[#0284c7] transition-colors mb-3"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M12 21s-7-5.5-7-11a7 7 0 1114 0c0 5.5-7 11-7 11z" />
                      <circle cx="12" cy="10" r="2.5" />
                    </svg>
                    {club.venue}
                  </a>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {club.teams.map((t) => {
                    const sheet = (club.team_sheets || []).find((p) => p.team === t.name)?.file;
                    const cls = `px-2 py-1 rounded text-[10px] font-ui border ${
                      t.league === "gold"
                        ? "border-[#f59e0b]/40 text-[#b45309]"
                        : "border-[#38bdf8]/40 text-[#0284c7]"
                    } bg-[#e3edf6]`;
                    if (sheet) {
                      return (
                        <a
                          key={t.name}
                          href={sheet}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`club-team-${club.slug}-${t.name}`}
                          className={`${cls} cursor-pointer hover:shadow-md hover:border-current transition-all underline decoration-dotted underline-offset-2`}
                          title="Vedi formazione"
                        >
                          {t.name}
                          {t.girone ? ` • ${t.league === "gold" ? "Gold" : "Silver"} ${t.girone}` : ""}
                        </a>
                      );
                    }
                    return (
                      <span key={t.name} data-testid={`club-team-${club.slug}-${t.name}`} className={cls}>
                        {t.name}
                        {t.girone ? ` • ${t.league === "gold" ? "Gold" : "Silver"} ${t.girone}` : ""}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      </main>

      <footer className="border-t border-[#e2e8f0] py-6 px-8 bg-[#d6e4f0] text-xs font-mono-x text-[#64748b] text-center">
        Coppa dei Club 2026-27 • Portale Ufficiale Calendari, Gironi Gold &amp; Silver • MSP Italia Umbria • Segreteria
        Generale:{" "}
        <a href="tel:3510262959" className="text-[#0284c7] hover:underline">
          3510262959
        </a>{" "}
        • Segreteria Coppa dei Club:{" "}
        <a href="mailto:coppadeiclubmspumbria@gmail.com" className="text-[#0284c7] hover:underline">
          coppadeiclubmspumbria@gmail.com
        </a>
      </footer>
    </div>
  );
}
